import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useT, LanguageSelector } from "@/i18n";
import { ShieldCheck, EnvelopeSimple, ArrowRight, LockKey, CircleNotch } from "@phosphor-icons/react";

const HERO =
  "https://images.pexels.com/photos/5466285/pexels-photo-5466285.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { user, loading, refresh } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (searchParams.get("error")) {
      toast.error("Sign-in is temporarily unavailable", { description: "Please try again in a little while." });
    }
    if (searchParams.get("activated")) {
      toast.success("Password set", { description: "Please sign in with your email and new password." });
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      await refresh();
      navigate(r.data?.dest || "/", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : t("invalid_login"));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background relative">
      <div className="absolute top-4 right-4 z-20">
        <LanguageSelector />
      </div>
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Rotary district collaboration" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70 mix-blend-multiply" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-white flex items-center justify-center">
              <ShieldCheck size={24} weight="fill" className="text-primary" />
            </div>
            <span className="font-display font-extrabold tracking-tight text-lg">{t("brand")}</span>
          </div>
          <div className="max-w-md">
            <p className="text-[11px] uppercase tracking-[0.3em] text-white/70 mb-4">{t("login_hero_kicker")}</p>
            <h1 className="font-display font-extrabold tracking-tighter text-4xl xl:text-5xl leading-[0.95]">
              {t("login_hero_title")}
            </h1>
            <p className="mt-6 text-white/80 leading-relaxed text-sm max-w-sm">
              {t("login_hero_sub")}
            </p>
          </div>
          <div className="flex items-center gap-2 text-white/60 text-xs">
            <LockKey size={16} weight="duotone" /> {t("login_hero_secure")}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-9 w-9 bg-primary flex items-center justify-center">
              <ShieldCheck size={20} weight="fill" className="text-white" />
            </div>
            <span className="font-display font-extrabold tracking-tight">{t("brand")}</span>
          </div>

          <div className="border border-black/10 dark:border-white/10 bg-surface">
            <form className="p-8" onSubmit={submit} data-testid="login-form">
              <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{t("signin_kicker")}</p>
              <h2 className="mt-2 font-display font-extrabold tracking-tighter text-3xl">{t("signin_title")}</h2>

              <label className="block mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("email")}</label>
              <input
                data-testid="login-email-input" type="email" required autoComplete="username"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full border border-border bg-background px-4 py-3 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <label className="block mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("password")}</label>
              <input
                data-testid="login-password-input" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full border border-border bg-background px-4 py-3 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {error && (
                <p data-testid="login-error" className="mt-3 text-sm text-red-700">{error}</p>
              )}
              <button
                data-testid="login-submit-button" type="submit" disabled={submitting}
                className="mt-5 w-full flex items-center justify-center gap-3 px-6 py-4 text-sm font-semibold uppercase tracking-[0.15em] transition-colors active:scale-[0.98] bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {submitting ? <CircleNotch size={18} className="animate-spin" weight="bold" /> : <EnvelopeSimple size={18} weight="bold" />}
                {t("signin_btn")}
                <ArrowRight size={18} weight="bold" />
              </button>
              <div className="mt-4 text-center">
                <Link data-testid="forgot-password-link" to="/forgot" className="text-sm text-primary hover:underline">
                  {t("forgot")}
                </Link>
              </div>
            </form>
            <div className="border-t border-black/10 dark:border-white/10 px-8 py-4">
              <span className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                <LockKey size={13} weight="duotone" /> {t("login_footer")}
              </span>
            </div>
          </div>
          <p className="mt-6 text-center text-[12px] text-muted-foreground" data-testid="login-footer">
            powered by <a href="https://commworks.online" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary hover:underline">commworks.online</a>
          </p>
        </div>
      </div>
    </div>
  );
}
